// Working out who miscounts from dispatch.stock_count_checks rows.
//
// A correction at a count usually means an EARLIER report was miscounted, not this one. So
// each count is judged by the next count of the same part: if that one had to correct
// Priority, whoever counted before probably got it wrong (or something left the shelf
// unrecorded in between).

// Counts that settled Priority's stock (a 'recount' row is a first count that was redone).
export const SETTLED = new Set(['matched', 'corrected']);

// A first count followed by no finished count of the same part, by the same person, within
// this long is treated as given up rather than recounted.
const RECOUNT_WINDOW_MS = 30 * 60 * 1000;

export const personOf = (row) => row.employee_name || row.user_email || 'Unknown';

// Same rule the stock-count function uses to ask for a recount
export const isBigDifference = (difference, expected, counted) => {
  const d = Math.abs(Number(difference) || 0);
  return d > 0 && d >= Math.max(3, Math.ceil(0.25 * Math.max(Math.abs(Number(expected) || 0), Number(counted) || 0)));
};

const time = (row) => new Date(row.created_at).getTime();

// Adds to each row:
//   next       - for a settled count, the next settled count of the same part (or null)
//   abandoned  - for a 'recount' row, true when it was never followed by a finished count
export const annotateCounts = (rows) => {
  const byPart = new Map();
  for (const row of rows) {
    byPart.set(row.part_name, [...(byPart.get(row.part_name) || []), row]);
  }
  const extra = new Map();
  for (const partRows of byPart.values()) {
    const ordered = [...partRows].sort((a, b) => time(a) - time(b));
    const settled = ordered.filter(r => SETTLED.has(r.status));
    settled.forEach((row, i) => extra.set(row.id, { next: settled[i + 1] || null }));
    for (const row of ordered.filter(r => r.status === 'recount')) {
      const finished = ordered.some(r => r !== row && r.status !== 'recount' && personOf(r) === personOf(row)
        && time(r) >= time(row) && time(r) - time(row) <= RECOUNT_WINDOW_MS);
      extra.set(row.id, { abandoned: !finished });
    }
  }
  return rows.map(row => ({ next: null, abandoned: false, ...row, ...extra.get(row.id) }));
};

// Per person: how their counts went, worst first.
//   checked   - their settled counts that have a later count of the same part
//   disputed  - of those, how many the next count had to correct
export const summariseByPerson = (annotated) => {
  const people = new Map();
  for (const row of annotated) {
    const name = personOf(row);
    const p = people.get(name) || { name, counts: 0, matched: 0, corrected: 0, recounts: 0, abandoned: 0, checked: 0, disputed: 0, disputedUnits: 0 };
    if (row.status === 'recount') {
      p.recounts += 1;
      if (row.abandoned) p.abandoned += 1;
    } else if (SETTLED.has(row.status)) {
      p.counts += 1;
      if (row.status === 'matched') p.matched += 1;
      else p.corrected += 1;
      if (row.next) {
        p.checked += 1;
        const nextDiff = Number(row.next.difference) || 0;
        if (nextDiff !== 0) {
          p.disputed += 1;
          p.disputedUnits += Math.abs(nextDiff);
        }
      }
    }
    people.set(name, p);
  }
  const rate = (p) => (p.checked > 0 ? p.disputed / p.checked : 0);
  return [...people.values()].sort((a, b) => rate(b) - rate(a) || b.disputedUnits - a.disputedUnits || a.name.localeCompare(b.name));
};

// Rows worth a look: big corrections, recounts, failures, or a count the next one disputed.
export const isProblem = (row) => row.status === 'recount' || row.status === 'failed'
  || isBigDifference(row.difference, row.expected, row.counted)
  || Boolean(row.next && Number(row.next.difference) !== 0);

export const signed = (n) => {
  const v = Number(n) || 0;
  return v > 0 ? `+${v}` : String(v);
};
